import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "../../../helpers";
import { SR_VOICE_DEFAULT_MIME } from "../sr-constants";
import { runVoiceSend } from "./sr-send-voice";
import { runTextSend } from "./sr-send-text";
import { fetchSrChat } from "./sr-chat-list";
import { makeSrAgentPosts } from "./sr-agent-api";
import {
    type SrChatMessage,
    newConversationId,
    welcome,
} from "./sr-chat-types";

export type { SrChatMessage, SrChatRole } from "./sr-chat-types";

export const useSrAgentChat = () => {
    const rootStore = useStore();
    const { projectid } = useParams();
    const [messages, setMessages] = useState<SrChatMessage[]>([welcome]);
    const [sending, setSending] = useState(false);
    const [conversationId, setConversationId] = useState(newConversationId);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const { postChat, postTranscribe } = useMemo(
        () => makeSrAgentPosts(rootStore.apiRequestHandler),
        [rootStore]
    );

    const clearChat = useCallback(() => {
        setMessages([welcome]);
        setConversationId(newConversationId());
    }, []);

    const sendMessage = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || sending || !projectid) return;
            setSending(true);
            await runTextSend({
                text: trimmed,
                projectid,
                prior: messagesRef.current,
                conversationId,
                postChat,
                setMessages,
            });
            setSending(false);
        },
        [conversationId, postChat, projectid, sending]
    );

    const sendVoice = useCallback(
        async (blob: Blob, mime = SR_VOICE_DEFAULT_MIME) => {
            if (!blob?.size || sending || !projectid) return;
            setSending(true);
            await runVoiceSend({
                blob,
                mime,
                projectid,
                messages: messagesRef.current,
                conversationId,
                postTranscribe,
                postChat,
                setMessages,
            });
            setSending(false);
        },
        [conversationId, postChat, postTranscribe, projectid, sending]
    );

    const loadChat = useCallback(
        async (id: string) => {
            if (!id || sending || !projectid) return;
            const msgs = await fetchSrChat(
                rootStore.apiRequestHandler,
                parseInt(projectid, 10),
                id
            );
            setConversationId(id);
            setMessages(msgs);
        },
        [projectid, rootStore, sending]
    );

    return {
        messages,
        sending,
        conversationId,
        sendMessage,
        sendVoice,
        clearChat,
        loadChat,
    };
};
